import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  OnDestroy, Optional,
  ViewChild
} from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { finalize, switchMap, takeUntil, tap } from "rxjs/operators";
import { fromEvent, Subscription } from "rxjs";
import { ModalsService } from "projects/common/src/lib/services/modals.service";

@Component({
  selector: 'app-signature',
  templateUrl: './signature.component.html',
  styleUrls: ['./signature.component.scss']
})
export class SignatureComponent implements AfterViewInit, OnDestroy {

  modalWidth: string;
  disabledModal = false;
  showSkeleton = false;
  drawn = false;

  onSave = new EventEmitter();

  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    event.preventDefault();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.modalWidth = window.innerWidth > 576 ? '476' : '244';
  }

  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  ctx!: CanvasRenderingContext2D;

  mouseSubscription!: Subscription;
  touchSubscription!: Subscription;

  get data() {
    return this.dialogData ?? this.modalsService.data;
  }

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private modalsService: ModalsService
  ) {
    this.modalWidth = window.innerWidth > 576 ? '476' : '244';
  }

  async save() {
    if (!this.disabledModal) {
      if (this.drawn) {
        this.disabledModal = true;
        this.onSave.emit();
        try {
          await this.data.saveSignature(this.ctx.canvas.toDataURL('image/png'));
          this.modalsService.close();
        } catch (e) {
          console.log(e);
        } finally {
          this.disabledModal = false;
        }
      } else {
        this.modalsService.close();
      }
    }
  }

  ngAfterViewInit(): void {
    this.ctx = this.canvas.nativeElement.getContext('2d')!;
    this.initMouseEvents();
    this.initTouchEvents();
  }

  ngOnDestroy() {
    this.mouseSubscription?.unsubscribe();
    this.touchSubscription?.unsubscribe();
  }

  close() {
    this.modalsService.close();
  }

  private handleStartDrawing(x: number, y: number): void {
    if (!this.disabledModal) {
      this.ctx.beginPath();
      this.ctx.strokeStyle = 'black';
      this.ctx.lineWidth = 2;
      this.ctx.lineJoin = 'round';
      this.ctx.moveTo(x, y);
    }
  }

  private handleDraw(x: number, y: number): void {
    if (!this.disabledModal) {
      this.drawn = true;
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
    }
  }

  private handleEndDrawing(): void {
    this.ctx.closePath();
  }

  private initMouseEvents(): void {
    const mouseDownStream = fromEvent(this.canvas.nativeElement, 'mousedown');
    const mouseMoveStream = fromEvent(this.canvas.nativeElement, 'mousemove');
    const mouseUpStream = fromEvent(window, 'mouseup');

    this.mouseSubscription = mouseDownStream.pipe(
      tap((event: any) => this.handleStartDrawing(event.offsetX, event.offsetY)),
      switchMap(() => mouseMoveStream.pipe(
        tap((event: any) => this.handleDraw(event.offsetX, event.offsetY)),
        takeUntil(mouseUpStream),
        finalize(() => this.handleEndDrawing())
      ))
    ).subscribe();
  }

  private initTouchEvents(): void {
    const touchStartStream = fromEvent(this.canvas.nativeElement, 'touchstart');
    const touchMoveStream = fromEvent(this.canvas.nativeElement, 'touchmove');
    const touchEndStream = fromEvent(window, 'touchend');

    this.touchSubscription = touchStartStream.pipe(
      tap((event: any) => {
        const touch = event.touches[0];
        event.preventDefault();
        const canvasRect = this.canvas.nativeElement.getBoundingClientRect();
        const offsetX = touch.clientX - canvasRect.left;
        const offsetY = touch.clientY - canvasRect.top;
        this.handleStartDrawing(offsetX, offsetY);
      }),
      switchMap(() => touchMoveStream.pipe(
        tap((event: any) => {
          const touch = event.touches[0];
          const canvasRect = this.canvas.nativeElement.getBoundingClientRect();
          const offsetX = touch.clientX - canvasRect.left;
          const offsetY = touch.clientY - canvasRect.top;
          this.handleDraw(offsetX, offsetY);
        }),
        takeUntil(touchEndStream),
        finalize(() => this.handleEndDrawing())
      ))
    ).subscribe();
  }
}
